<template>
  <div>
    <b-card>
      <b-overlay :show="formShow" rounded="sm" no-fade>
        <b-card>
          <b-row>
            <b-col cols="12" xl="2">
              <h6 class="mb-2">Start Date</h6>
              <flat-pickr v-model="startDate" title="Start date" style="width:100%; background-color:white" :config="{ dateFormat: 'Y-m-d', locale: { firstDayOfWeek: 1 } }" class="form-control invoice-edit-input" />
            </b-col>

            <b-col cols="12" xl="2">
              <h6 class="mb-2">End Date</h6>
              <flat-pickr v-model="endDate" title="End date" style="width:100%; background-color:white" :config="{ dateFormat: 'Y-m-d', locale: { firstDayOfWeek: 1 } }" class="form-control invoice-edit-input" />
            </b-col>

            <b-col cols="12" xl="4">
              <div class="d-flex mt-2">
                <div class="mr-2 mt-2">
                  <b-form-checkbox v-model="isNavMenuHidden" name="is-vertical-menu-collapsed" class="mr-0" inline>Collapse Menu</b-form-checkbox>
                </div>
              </div>
            </b-col>

            <b-col cols="12" md="4" class="d-flex align-items-center justify-content-end mb-1 mb-md-0">
              <b-button variant="primary" class="mr-1" :disabled="exporting || !invoiceData.length" @click="exportExcel">
                <b-spinner v-if="exporting" small class="mr-50" />
                Export
              </b-button>
            </b-col>
          </b-row>

          <div class="report-summary mt-2">
            <span>Materials recorded on timesheets between {{ startDate }} and {{ endDate }}.</span>
            <span v-if="invoiceData.length">
              <b>{{ invoiceData.length }}</b> {{ invoiceData.length === 1 ? 'entry' : 'entries' }} · Total <b>{{ formatPrice(totalCost) }}</b>
              <span v-if="unpricedCount" class="text-warning"> · {{ unpricedCount }} without a unit cost</span>
            </span>
          </div>

          <b-row style="overflow-x:auto" class="mt-1">
            <b-col cols="12">
              <b-table
                hover
                :items="invoiceData"
                :fields="materialFields"
                small
                thead-class="green-bg text-white"
                show-empty
                empty-text="No materials recorded on timesheets in this date range."
              >
                <template #cell(Project)="{ item }">
                  <b>{{ item.Project }}</b>
                  <small v-if="item.ProjectName" class="d-block text-muted">{{ item.ProjectName }}</small>
                </template>

                <template #cell(Part)="{ item }">{{ item.Part || '—' }}</template>

                <template #cell(Qty)="{ item }">{{ formatQty(item.Qty) }}</template>

                <template #cell(UnitCost)="{ item }">{{ item.UnitCost === null ? '—' : formatUnitCost(item.UnitCost) }}</template>

                <template #cell(TotalCost)="{ item }">{{ item.TotalCost === null ? '—' : formatPrice(item.TotalCost) }}</template>

                <template #cell(Note)="{ item }">
                  <span v-if="item.Note" v-b-tooltip.hover.top="item.Note" class="note-cell">{{ item.Note }}</span>
                  <span v-else>—</span>
                </template>

                <template #cell(Account)="{ item }">
                  <span class="account-cell">{{ item.Account || '—' }}</span>
                </template>
              </b-table>
            </b-col>
          </b-row>
        </b-card>
      </b-overlay>
    </b-card>
  </div>
</template>

<script>
import { onUnmounted } from '@vue/composition-api';
import store from '@/store';
import { BRow, BCol, BCard, BButton, BFormCheckbox, BOverlay, BTable, BSpinner, VBTooltip } from 'bootstrap-vue';
import flatPickr from 'vue-flatpickr-component';
import billingStoreModule from './billingStoreModule';
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue';
import useAppCustomizer from '@core/layouts/components/app-customizer/useAppCustomizer';

function ymd(date) {
  return date.getFullYear() + '-' + ('0' + (date.getMonth() + 1)).slice(-2) + '-' + ('0' + date.getDate()).slice(-2);
}

// Reports > Materials Used: materials staff recorded on their weekly timesheets.
export default {
  components: {
    BRow,
    BCol,
    BCard,
    BButton,
    BFormCheckbox,
    BOverlay,
    BTable,
    BSpinner,
    flatPickr,
  },
  directives: {
    'b-tooltip': VBTooltip,
  },

  setup() {
    const { isNavMenuHidden } = useAppCustomizer();
    const BILLING_STORE_MODULE_NAME = 'billing';

    if (!store.hasModule(BILLING_STORE_MODULE_NAME)) store.registerModule(BILLING_STORE_MODULE_NAME, billingStoreModule);

    onUnmounted(() => {
      if (store.hasModule(BILLING_STORE_MODULE_NAME)) store.unregisterModule(BILLING_STORE_MODULE_NAME);
    });

    return { isNavMenuHidden };
  },

  data() {
    // Defaults to last month.
    const today = new Date();
    const firstOfLastMonth = new Date(today.getFullYear(), today.getMonth() - 1, 1);
    const lastOfLastMonth = new Date(today.getFullYear(), today.getMonth(), 0);

    return {
      startDate: ymd(firstOfLastMonth),
      endDate: ymd(lastOfLastMonth),
      invoiceData: [],
      totalCost: 0,
      unpricedCount: 0,
      formShow: false,
      exporting: false,
      materialFields: [
        { key: 'Date', label: 'Date', sortable: true, tdClass: 'text-nowrap', thStyle: 'min-width:100px;' },
        { key: 'Project', label: 'Project', sortable: true, thStyle: 'min-width:160px;' },
        { key: 'Part', label: 'Part', sortable: false, thStyle: 'min-width:110px;' },
        { key: 'Employee', label: 'Employee', sortable: true, thStyle: 'min-width:140px;' },
        { key: 'Material', label: 'Material', sortable: true, thStyle: 'min-width:150px;' },
        { key: 'Qty', label: 'Qty', sortable: false, class: 'text-right', thStyle: 'min-width:70px;' },
        { key: 'Unit', label: 'Unit', sortable: false, thStyle: 'min-width:60px;' },
        { key: 'UnitCost', label: 'Unit Cost', sortable: false, class: 'text-right', thStyle: 'min-width:100px;' },
        { key: 'TotalCost', label: 'Total Cost', sortable: true, class: 'text-right', thStyle: 'min-width:100px;' },
        { key: 'Note', label: 'Note', sortable: false, thStyle: 'min-width:160px;' },
        { key: 'Account', label: 'Account', sortable: false, thStyle: 'min-width:150px;' },
      ],
    };
  },

  watch: {
    startDate(value) {
      if (value) this.getInvoiceData();
    },
    endDate(value) {
      if (value) this.getInvoiceData();
    },
  },

  mounted() {
    this.getInvoiceData();
  },

  destroyed() {
    this.isNavMenuHidden = false;
  },

  methods: {
    toast(title, text, variant) {
      this.$toast({ component: ToastificationContent, props: { title, text, icon: 'AlertTriangleIcon', variant } });
    },

    formatPrice(value) {
      if (value === null || value === undefined) return '';
      return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(value);
    },

    // Unit prices can be fractions of a cent (e.g. $0.0350/in), so keep up to 4 decimals.
    formatUnitCost(value) {
      if (value === null || value === undefined) return '';
      return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 2, maximumFractionDigits: 4 }).format(value);
    },

    formatQty(value) {
      return value === null || value === undefined ? '' : Number(value).toLocaleString('en-US', { maximumFractionDigits: 3 });
    },

    rangeIsValid() {
      return this.startDate && this.endDate && this.startDate <= this.endDate;
    },

    getInvoiceData() {
      if (!this.rangeIsValid()) return;
      this.formShow = true;
      store
        .dispatch('billing/getMaterialsUsed', { startDate: this.startDate, endDate: this.endDate })
        .then((response) => {
          this.invoiceData = response.data.rows || [];
          this.totalCost = response.data.totalCost || 0;
          this.unpricedCount = response.data.unpricedCount || 0;
          this.formShow = false;
        })
        .catch((error) => {
          console.error(error);
          this.formShow = false;
          this.toast('Report not loaded', 'Please check the dates and try again.', 'danger');
        });
    },

    exportExcel() {
      if (!this.rangeIsValid()) return;
      this.exporting = true;
      store
        .dispatch('billing/exportMaterials', { startDate: this.startDate, endDate: this.endDate })
        .then((res) => {
          const blob = new Blob([res.data], { type: 'application/octet-stream' });
          const url = window.URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.href = url;
          link.setAttribute('download', `materials-used ${this.startDate} to ${this.endDate}.xlsx`);
          document.body.appendChild(link);
          link.click();
          link.remove();
          window.URL.revokeObjectURL(url);
          this.exporting = false;
        })
        .catch((error) => {
          console.error(error);
          this.exporting = false;
          this.toast('Export failed', 'The report could not be exported. Please try again.', 'danger');
        });
    },
  },
};
</script>

<style lang="scss">
@import '@core/scss/vue/libs/vue-flatpicker.scss';
</style>

<style lang="scss" scoped>
.report-summary {
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  gap: 4px 16px;
  font-size: 13px;
  color: #5e5873;
}

.note-cell {
  display: inline-block;
  max-width: 220px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  vertical-align: bottom;
  cursor: help;
}

.account-cell {
  font-family: SFMono-Regular, Menlo, Consolas, monospace;
  font-size: 12px;
}
</style>
